import { api, type ApiResponse, type PaginatedApiResponse } from '../client';

export interface IncomingPayment {
  uuid: string;
  type: 'pending';
  employer: {
    uuid: string;
    name: string;
  };
  amount: number;
  donation_count?: number;
  payment_date: string | null;
  reference_number: string | null;
  invoice_number: string | null;
  notes: string | null;
  created_at: string | null;
}

export interface CharityDonators {
  charity: {
    uuid: string;
    name: string;
    verified_bsb_no?: string | null;
    verified_acc_no?: string | null;
    verified_acc_name?: string | null;
  };
  donators: Array<{
    uuid: string;
    name: string;
    hours: number;
    amount: number;
    leave_value: number;
    employer_top_up: number;
    contribution: number;
    approved_at: string | null;
  }>;
  totalAmount: number;
  totalHours: number;
  totalContribution: number;
}

export interface IncomingPaymentDetails {
  payment_uuid: string;
  type: 'paid' | 'pending';
  employer: {
    uuid: string;
    name: string;
    abn?: string;
    contact_name?: string;
    contact_email?: string;
    contact_phone?: string;
    head_office_location?: string;
  };
  payment_date: string | null;
  reference_number: string | null;
  invoice_number: string | null;
  notes: string | null;
  totalAmount: number;
  totalHours: number;
  totalDonations: number;
  totalContribution: number;
  totalCharities: number;
  charities: CharityDonators[];
}

export interface OutgoingPayment {
  uuid: string;
  type: 'paid' | 'to_make';
  charity: {
    uuid: string;
    name: string;
  };
  amount: number;
  l2g_fee_percentage: number;
  l2g_fee_amount: number;
  employer_payment_uuid: string | null;
  employer_name: string | null;
  status: string;
  transaction_id: string | null;
  transaction_date: string | null;
  created_at: string;
  payout_bsb_no?: string | null;
  payout_acc_no?: string | null;
  payout_acc_name?: string | null;
  charity_marked_received_at?: string | null;
  charity_confirmed_receipt?: boolean;
}

export interface PaymentFilters {
  page?: number;
  per_page?: number;
  employer_uuid?: string;
  charity_uuid?: string;
  status?: string;
  statuses?: string[];
  /** Outgoing payments only: restrict to payouts the charity has confirmed receipt of */
  confirmed_only?: boolean;
  start_date?: string;
  end_date?: string;
  phrase?: string;
}

export interface UpdateOutgoingPaymentData {
  l2g_fee_percentage?: number;
  l2g_fee_amount?: number;
  mark_as_paid?: boolean;
  transaction_id?: string;
  transaction_date?: string;
  employer_payment_uuid?: string; // For creating payment if it doesn't exist
  charity_uuid?: string; // For creating payment if it doesn't exist
  amount?: number; // For creating payment if it doesn't exist
}

const PaymentsService = {
  async getIncomingPayments(filters: PaymentFilters = {}): Promise<PaginatedApiResponse<IncomingPayment>> {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.per_page) params.append('per_page', filters.per_page.toString());
    if (filters.employer_uuid) params.append('employer_uuid', filters.employer_uuid);
    if (filters.charity_uuid) params.append('charity_uuid', filters.charity_uuid);
    if (filters.statuses && filters.statuses.length > 0) {
      filters.statuses.forEach(status => {
        params.append('statuses[]', status);
      });
    }
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.phrase) params.append('phrase', filters.phrase);

    const response = await api.get<PaginatedApiResponse<IncomingPayment>>(`/admin/payments/incoming?${params.toString()}`);
    return response.data;
  },

  async getOutgoingPayments(filters: PaymentFilters = {}): Promise<PaginatedApiResponse<OutgoingPayment>> {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.per_page) params.append('per_page', filters.per_page.toString());
    if (filters.employer_uuid) params.append('employer_uuid', filters.employer_uuid);
    if (filters.charity_uuid) params.append('charity_uuid', filters.charity_uuid);
    if (filters.status) params.append('status', filters.status);
    if (filters.confirmed_only) params.append('confirmed_only', '1');
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.phrase) params.append('phrase', filters.phrase);

    const response = await api.get<PaginatedApiResponse<OutgoingPayment>>(`/admin/payments/outgoing?${params.toString()}`);
    return response.data;
  },

  async updateOutgoingPayment(uuid: string, data: UpdateOutgoingPaymentData): Promise<ApiResponse<OutgoingPayment>> {
    const response = await api.put<ApiResponse<OutgoingPayment>>(`/admin/payments/outgoing/${uuid}`, data);
    return response.data;
  },

  async getIncomingPaymentDetails(uuid: string): Promise<ApiResponse<IncomingPaymentDetails>> {
    const response = await api.get<ApiResponse<IncomingPaymentDetails>>(`/admin/payments/incoming/${uuid}`);
    return response.data;
  },
};

export default PaymentsService;

